/**
 * Input bitfield encoding/decoding and control logic.
 * Mouse-aim control scheme: ship auto-rotates toward targetAngle,
 * WASD = forward/reverse/strafe, mouse = aim direction.
 */
import { INPUT_THRUST, INPUT_STRAFE_LEFT, INPUT_STRAFE_RIGHT, INPUT_BRAKE, INPUT_AFTERBURNER, INPUT_FIRE, FIGHTER_THRUST, FIGHTER_STRAFE_THRUST, AFTERBURNER_MULT, FIGHTER_ROTATION_SPEED, BATTERY_DRAIN_PER_SEC, BATTERY_REGEN_PER_SEC, BATTERY_MAX, TICK_DELTA, } from './constants.js';
export function encodeInput(keys) {
    let bitfield = 0;
    if (keys.thrust)
        bitfield |= INPUT_THRUST;
    if (keys.strafeLeft)
        bitfield |= INPUT_STRAFE_LEFT;
    if (keys.strafeRight)
        bitfield |= INPUT_STRAFE_RIGHT;
    if (keys.brake)
        bitfield |= INPUT_BRAKE;
    if (keys.afterburner)
        bitfield |= INPUT_AFTERBURNER;
    if (keys.fire)
        bitfield |= INPUT_FIRE;
    return bitfield;
}
export function decodeInput(bitfield) {
    return {
        thrust: (bitfield & INPUT_THRUST) !== 0,
        strafeLeft: (bitfield & INPUT_STRAFE_LEFT) !== 0,
        strafeRight: (bitfield & INPUT_STRAFE_RIGHT) !== 0,
        brake: (bitfield & INPUT_BRAKE) !== 0,
        afterburner: (bitfield & INPUT_AFTERBURNER) !== 0,
        fire: (bitfield & INPUT_FIRE) !== 0,
    };
}
/**
 * Normalize an angle to [-PI, PI].
 */
function normalizeAngle(angle) {
    while (angle > Math.PI)
        angle -= 2 * Math.PI;
    while (angle < -Math.PI)
        angle += 2 * Math.PI;
    return angle;
}
/**
 * Calculate thrust force vector, rotation chase, and battery changes from input.
 *
 * Mouse-aim rotation: ship chases targetAngle, clamped by FIGHTER_ROTATION_SPEED.
 * Force: 2D rotation matrix transforms ship-local (forward + lateral) to world space.
 * Afterburner boosts all thrust directions.
 */
export function resolveInput(bitfield, rotation, battery, targetAngle) {
    const input = decodeInput(bitfield);
    // Rotation: chase targetAngle, clamped by max rotation speed per tick
    const angleDiff = normalizeAngle(targetAngle - rotation);
    const maxRot = FIGHTER_ROTATION_SPEED * TICK_DELTA;
    const rotationDelta = Math.abs(angleDiff) <= maxRot
        ? angleDiff
        : Math.sign(angleDiff) * maxRot;
    // Determine forward/reverse thrust (ship-local forward axis)
    // Afterburner only adds forward thrust when no other directional inputs are active.
    // With strafe/brake active, afterburner just boosts those thrusters.
    const wantsThrust = input.thrust || (input.afterburner && !input.brake && !input.strafeLeft && !input.strafeRight);
    const wantsBrake = input.brake;
    const wantsAfterburner = input.afterburner;
    const wantsStrafeLeft = input.strafeLeft;
    const wantsStrafeRight = input.strafeRight;
    let forwardThrust = 0;
    let isBraking = false;
    if (wantsThrust && !wantsBrake) {
        forwardThrust = FIGHTER_THRUST;
    }
    else if (wantsBrake && !wantsThrust) {
        forwardThrust = -FIGHTER_THRUST;
        isBraking = true;
    }
    else if (wantsThrust && wantsBrake) {
        // Both pressed: brake wins (reverse thrust)
        forwardThrust = -FIGHTER_THRUST;
        isBraking = true;
    }
    // Determine lateral thrust (ship-local lateral axis)
    let lateralThrust = 0;
    if (wantsStrafeLeft && !wantsStrafeRight) {
        lateralThrust = -FIGHTER_STRAFE_THRUST;
    }
    else if (wantsStrafeRight && !wantsStrafeLeft) {
        lateralThrust = FIGHTER_STRAFE_THRUST;
    }
    // Both strafe keys cancel out: lateralThrust stays 0
    const anyThrust = forwardThrust !== 0 || lateralThrust !== 0;
    // Afterburner: boosts all thrust directions
    let isAfterburning = false;
    let newBattery = battery;
    if (wantsAfterburner && anyThrust && battery > 0) {
        forwardThrust *= AFTERBURNER_MULT;
        lateralThrust *= AFTERBURNER_MULT;
        isAfterburning = true;
        newBattery = Math.max(0, battery - BATTERY_DRAIN_PER_SEC * TICK_DELTA);
    }
    else if (!wantsAfterburner) {
        // Regenerate battery when not using afterburner
        newBattery = Math.min(BATTERY_MAX, battery + BATTERY_REGEN_PER_SEC * TICK_DELTA);
    }
    // Transform ship-local forces to world space using 2D rotation matrix.
    // Ship-local: forward = (1,0), right = (0,1) relative to ship heading.
    // World: fx = forward * cos(θ) - lateral * sin(θ)
    //         fy = forward * sin(θ) + lateral * cos(θ)
    const cosR = Math.cos(rotation);
    const sinR = Math.sin(rotation);
    const fx = forwardThrust * cosR - lateralThrust * sinR;
    const fy = forwardThrust * sinR + lateralThrust * cosR;
    return {
        force: { x: fx, y: fy },
        rotationDelta,
        newBattery,
        isAfterburning,
        isThrusting: forwardThrust > 0,
        isBraking,
        isStrafingLeft: wantsStrafeLeft && !wantsStrafeRight,
        isStrafingRight: wantsStrafeRight && !wantsStrafeLeft,
        isFiring: input.fire,
    };
}
//# sourceMappingURL=input.js.map